import { SafeCapType } from 'shared';
import { z } from 'zod';

import { getTranslation } from '../../../utils/get-translation.util';

export const MAX_FILE_SIZE = 1024 * 1024 * 10;

export const ACCEPTED_FILE_MIME_TYPES = [
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'text/csv',
  'image/png',
  'image/jpeg',
];
export const fileSchemaMessage = getTranslation('FILE_SIZE_TOO_LARGE');

export const stepOne = z.object({
  stakeholder: z.object({
    fullName: z
      .string()
      .trim()
      .min(1, { message: getTranslation('REQUIRED') }),
    id: z
      .string()
      .trim()
      .min(1, { message: getTranslation('REQUIRED') }),
  }),
  investment: z.coerce.number().min(1, { message: getTranslation('INVESTMENT_AMOUNT_REQUIRED') }),
  issueDate: z.coerce.date(),
});

const isEnabledSection = (value: unknown): boolean =>
  typeof value === 'object' && value !== null && 'enabled' in value && value.enabled === true;

/**
 * Step two keeps every switch's inputs mounted (for its reveal animation), so a switched-off
 * section still holds its registered-but-empty fields — `{ discountValue: undefined }` with no
 * `enabled`. Only a switched-on section is validated; an off one parses to `undefined`, so it can
 * neither block Next nor leak a stale value into the payload.
 */
const whenEnabled = <T extends z.ZodType>(section: T) =>
  z.preprocess((value) => (isEnabledSection(value) ? value : undefined), section.optional());

export const stepTwo = z
  .object({
    cap: whenEnabled(
      z.object({
        enabled: z.boolean(),
        capValue: z.coerce
          .number({ message: getTranslation('PLEASE_ENTER_NUMERIC_VALUE') })
          .min(1, { message: getTranslation('REQUIRED') }),
        // Mutually exclusive by construction (one radio group); required whenever the cap is on.
        capType: z.enum(SafeCapType, { message: getTranslation('CAP_TYPE_REQUIRED') }),
      }),
    ),
    discount: whenEnabled(
      z.object({
        enabled: z.boolean(),
        discountValue: z.coerce
          .number()
          .min(1, { message: 'Minimum 1%' })
          .max(100, { message: getTranslation('MAXIMUM_100') }),
      }),
    ),
    floor: whenEnabled(
      z
        .object({
          enabled: z.boolean(),
          floorValue: z
            .union([
              z.coerce
                .number({ message: getTranslation('PLEASE_ENTER_NUMERIC_VALUE') })
                .min(1, { message: getTranslation('REQUIRED') }),
              z.literal(''),
            ])
            .optional(),
          maturityDate: z.coerce.date().optional(),
        })
        .refine(
          (data) =>
            (data.floorValue !== '' && data.floorValue !== undefined) ||
            data.maturityDate !== undefined,
          {
            message: `${getTranslation('FLOOR_OR_MATURITY_REQUIRED')}`,
            path: ['floor'],
          },
        ),
    ),
  })
  .refine(
    (data) => {
      return data.cap?.enabled || data.discount?.enabled;
    },
    {
      message: `${getTranslation('CAP_OR_DISCOUNT_ENABLED')}`,
      path: ['cap'],
    },
  );

export const stepThree = z
  .object({
    additionalNotes: z
      .string()
      .max(3000, getTranslation('MAXIMUM_CHARACTERS', { count: 3000 }))
      .optional(),
    files: z
      .object({
        docLink: z.string(),
        loadProgress: z.number().max(100),
        abort: z.function(),
        id: z.string(),
        doc: z
          .custom<File>()
          .or(
            z.object({
              size: z.number(),
              type: z.string(),
              name: z.string(),
            }),
          )
          .refine(
            ({ type }) => ACCEPTED_FILE_MIME_TYPES.includes(type),
            getTranslation('FILE_FORMAT_NOT_SUPPORTED'),
          )
          .refine(({ size }) => size <= MAX_FILE_SIZE, fileSchemaMessage),
      })
      .array()

      .optional(),
  })
  .optional();

export const formSchema = z.object({
  stepOne,
  stepTwo,
  stepThree,
});

export type FormSchema = z.infer<typeof formSchema>;
